import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  PagedFlightAudit,
  PagedFlightBusinessPersonAudit,
  PagedFlightCrewMemberAudit,
  PagedFlightCrewMemberCateringAudit,
  PagedFlightCrewMemberHotelAudit,
  PagedFlightCrewMemberTransportationAudit,
  PagedFlightPassengerAudit,
  PagedFlightPassengerCateringAudit,
  PagedFlightPassengerHotelAudit,
  PagedFlightPassengerTransportationAudit,
  PagedLegNoteAudit,
  PagedFlightFuelAudit,
  PagedScheduledAircraftTripAudit,
  PagedScheduledAircraftTripSkeleton,
  PagedScheduledEventAudit,
  PagedScheduledFlightAudit,
  ScheduledAircraftTripSkeleton,
  PagedAuditLogLite,
  DeletedTripItem,
  PagedDeletedTripLegs,
  PagedTripLegs,
  PagedFlightCrewMembers,
  PagedFlightPassengers,
  PagedFlightSchedulers
} from './+models/trip-audit.model';
import { PagedAuditResponse } from './+models/audit-log.model';

@Injectable()
export class TripAuditService {
  private readonly apiUrl = '/sdapi/pre-flight/ScheduledAircraftTripsAudit';
  private readonly apiUrlV2 = '/sdapi/pre-flight';

  constructor(private readonly http: HttpClient) {}

  getDeletedScheduledTripSkeletons(
    pageNumber?: number,
    pageSize?: number,
    lastAuditLogId?: number | null
  ) {
    if (!pageNumber) {
      pageNumber = 1;
    }
    if (!pageSize) {
      pageSize = 25;
    }
    let url = `${this.apiUrl}/DeletedScheduledTripSkeletons?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    if (lastAuditLogId) {
      url = `${url}&lastAuditLogId=${lastAuditLogId}`;
    }
    return this.http.get<PagedScheduledAircraftTripSkeleton>(url);
  }

  getScheduledTripSkeleton(scheduledAircraftTripId: number) {
    const url = `${this.apiUrl}/ScheduledTripSkeleton/${scheduledAircraftTripId}`;
    return this.http.get<ScheduledAircraftTripSkeleton>(url);
  }

  getScheduledTripAudits(
    scheduledTripId: number,
    pageNumber?: number,
    pageSize?: number,
    lastAuditLogId?: number | null
  ) {
    return this.getAuditPage<PagedScheduledAircraftTripAudit>(
      `/${scheduledTripId}`,
      pageNumber,
      pageSize,
      lastAuditLogId
    );
  }

  getScheduledFlightAudits(
    scheduledFlightId: number,
    pageNumber?: number,
    pageSize?: number,
    lastAuditLogId?: number | null
  ) {
    return this.getAuditPage<PagedScheduledFlightAudit>(
      `/ScheduledFlight/${scheduledFlightId}`,
      pageNumber,
      pageSize,
      lastAuditLogId
    );
  }

  getFlightAudits(
    flightId: number,
    pageNumber?: number,
    pageSize?: number,
    lastAuditLogId?: number | null
  ) {
    return this.getAuditPage<PagedFlightAudit>(
      `/Flight/${flightId}`,
      pageNumber,
      pageSize,
      lastAuditLogId
    );
  }

  getFlightPassengerAudits(
    flightPassengerId: number,
    pageNumber?: number,
    pageSize?: number,
    lastAuditLogId?: number | null
  ) {
    return this.getAuditPage<PagedFlightPassengerAudit>(
      `/FlightPassenger/${flightPassengerId}`,
      pageNumber,
      pageSize,
      lastAuditLogId
    );
  }

  getFlightPassengerCateringAudits(
    flightPassengerCateringId: number,
    pageNumber?: number,
    pageSize?: number,
    lastAuditLogId?: number | null
  ) {
    return this.getAuditPage<PagedFlightPassengerCateringAudit>(
      `/FlightPassengerCatering/${flightPassengerCateringId}`,
      pageNumber,
      pageSize,
      lastAuditLogId
    );
  }

  getFlightPassengerHotelAudits(
    flightPassengerHotelId: number,
    pageNumber?: number,
    pageSize?: number,
    lastAuditLogId?: number | null
  ) {
    return this.getAuditPage<PagedFlightPassengerHotelAudit>(
      `/FlightPassengerHotel/${flightPassengerHotelId}`,
      pageNumber,
      pageSize,
      lastAuditLogId
    );
  }

  getFlightPassengerTransportationAudits(
    flightPassengerTransportationId: number,
    pageNumber?: number,
    pageSize?: number,
    lastAuditLogId?: number | null
  ) {
    return this.getAuditPage<PagedFlightPassengerTransportationAudit>(
      `/FlightPassengerTransportation/${flightPassengerTransportationId}`,
      pageNumber,
      pageSize,
      lastAuditLogId
    );
  }

  getFlightCrewMemberAudits(
    flightCrewMemberId: number,
    pageNumber?: number,
    pageSize?: number,
    lastAuditLogId?: number | null
  ) {
    return this.getAuditPage<PagedFlightCrewMemberAudit>(
      `/FlightCrewMember/${flightCrewMemberId}`,
      pageNumber,
      pageSize,
      lastAuditLogId
    );
  }

  getFlightCrewMemberCateringAudits(
    flightCrewMemberCateringId: number,
    pageNumber?: number,
    pageSize?: number,
    lastAuditLogId?: number | null
  ) {
    return this.getAuditPage<PagedFlightCrewMemberCateringAudit>(
      `/FlightCrewMemberCatering/${flightCrewMemberCateringId}`,
      pageNumber,
      pageSize,
      lastAuditLogId
    );
  }

  getFlightCrewMemberHotelAudits(
    flightCrewMemberHotelId: number,
    pageNumber?: number,
    pageSize?: number,
    lastAuditLogId?: number | null
  ) {
    return this.getAuditPage<PagedFlightCrewMemberHotelAudit>(
      `/FlightCrewMemberHotel/${flightCrewMemberHotelId}`,
      pageNumber,
      pageSize,
      lastAuditLogId
    );
  }

  getFlightCrewMemberTransportationAudits(
    flightCrewMemberTransportationId: number,
    pageNumber?: number,
    pageSize?: number,
    lastAuditLogId?: number | null
  ) {
    return this.getAuditPage<PagedFlightCrewMemberTransportationAudit>(
      `/FlightCrewMemberTransportation/${flightCrewMemberTransportationId}`,
      pageNumber,
      pageSize,
      lastAuditLogId
    );
  }

  getFlightBusinessPersonAudits(
    flightBusinessPersonId: number,
    pageNumber?: number,
    pageSize?: number,
    lastAuditLogId?: number | null
  ) {
    return this.getAuditPage<PagedFlightBusinessPersonAudit>(
      `/FlightBusinessPerson/${flightBusinessPersonId}`,
      pageNumber,
      pageSize,
      lastAuditLogId
    );
  }

  getScheduledEventAudits(
    scheduledEventId: number,
    pageNumber?: number,
    pageSize?: number,
    lastAuditLogId?: number | null
  ) {
    return this.getAuditPage<PagedScheduledEventAudit>(
      `/ScheduledEvent/${scheduledEventId}`,
      pageNumber,
      pageSize,
      lastAuditLogId
    );
  }

  getLegNoteAudits(
    legNoteId: number,
    pageNumber?: number,
    pageSize?: number,
    lastAuditLogId?: number | null
  ) {
    return this.getAuditPage<PagedLegNoteAudit>(
      `/LegNote/${legNoteId}`,
      pageNumber,
      pageSize,
      lastAuditLogId
    );
  }

  getFlightFuelAudits(
    flightFuelId: number,
    pageNumber?: number,
    pageSize?: number,
    lastAuditLogId?: number | null
  ) {
    return this.getAuditPage<PagedFlightFuelAudit>(
      `/FlightFuel/${flightFuelId}`,
      pageNumber,
      pageSize,
      lastAuditLogId
    );
  }

  private getAuditPage<T extends PagedAuditResponse>(
    subRoute: string,
    pageNumber?: number,
    pageSize?: number,
    lastAuditLogId?: number | null
  ) {
    if (!pageNumber) {
      pageNumber = 1;
    }
    if (!pageSize) {
      pageSize = 10;
    }
    let url = `${this.apiUrl}${subRoute}?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    if (lastAuditLogId) {
      url = `${url}&lastAuditLogId=${lastAuditLogId}`;
    }
    return this.http.get<T>(url);
  }

  getDeletedTrips(pageNumber = 1, pageSize = 10) {
    const url = `${this.apiUrlV2}/audit/trips/deleted?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    return this.http.get<{ totalCount: number; pageSize: number; pageNumber: number; resultSet: DeletedTripItem[] }>(url);
  }

  getTripLogs(tripId: number, pageNumber = 1, pageSize = 10, lastId?: number) {
    let url = `${this.apiUrlV2}/audit/trips/${tripId}/logs?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    if (lastId) {
      url = `${url}&lastId=${lastId}`;
    }
    return this.http.get<PagedAuditLogLite>(url);
  }

  getTripLegs(tripId: number, pageNumber = 1, pageSize = 10) {
    const url = `${this.apiUrlV2}/audit/trips/${tripId}/legs?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    return this.http.get<PagedTripLegs>(url);
  }

  getDeletedTripLegs(tripId: number, pageNumber = 1, pageSize = 10) {
    const url = `${this.apiUrlV2}/audit/trips/deleted/${tripId}/legs?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    return this.http.get<PagedDeletedTripLegs>(url);
  }

  getEventLogs(eventId: number, pageNumber = 1, pageSize = 10, lastId?: number) {
    let url = `${this.apiUrlV2}/audit/events/${eventId}/logs?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    if (lastId) {
      url = `${url}&lastId=${lastId}`;
    }
    return this.http.get<PagedAuditLogLite>(url);
  }

  getFlightLogs(flightId: number, pageNumber = 1, pageSize = 10, lastId?: number) {
    let url = `${this.apiUrlV2}/audit/flights/${flightId}/logs?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    if (lastId) {
      url = `${url}&lastId=${lastId}`;
    }
    return this.http.get<PagedAuditLogLite>(url);
  }

  getFuelLogs(fuelId: number, pageNumber = 1, pageSize = 10, lastId?: number) {
    let url = `${this.apiUrlV2}/audit/fuel/${fuelId}/logs?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    if (lastId) {
      url = `${url}&lastId=${lastId}`;
    }
    return this.http.get<PagedAuditLogLite>(url);
  }

  getFlightCrewMembers(flightId: number, pageNumber = 1, pageSize = 10) {
    const url = `${this.apiUrlV2}/audit/flights/deleted/${flightId}/crew?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    return this.http.get<PagedFlightCrewMembers>(url);
  }

  getScheduledFlightCrewMembers(flightId: number, pageNumber = 1, pageSize = 10) {
    const url = `${this.apiUrlV2}/audit/flights/${flightId}/crew?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    return this.http.get<PagedFlightCrewMembers>(url);
  }

  getCrewLogs(crewId: number, pageNumber = 1, pageSize = 10, lastId?: number) {
    let url = `${this.apiUrlV2}/audit/crew/${crewId}/logs?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    if (lastId) {
      url = `${url}&lastId=${lastId}`;
    }
    return this.http.get<PagedAuditLogLite>(url);
  }

  getCrewCateringLogs(cateringId: number, pageNumber = 1, pageSize = 10, lastId?: number) {
    let url = `${this.apiUrlV2}/audit/crew/catering/${cateringId}/logs?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    if (lastId) {
      url = `${url}&lastId=${lastId}`;
    }
    return this.http.get<PagedAuditLogLite>(url);
  }

  getCrewHotelLogs(hotelId: number, pageNumber = 1, pageSize = 10, lastId?: number) {
    let url = `${this.apiUrlV2}/audit/crew/hotels/${hotelId}/logs?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    if (lastId) {
      url = `${url}&lastId=${lastId}`;
    }
    return this.http.get<PagedAuditLogLite>(url);
  }

  getCrewTransportationLogs(transportationId: number, pageNumber = 1, pageSize = 10, lastId?: number) {
    let url = `${this.apiUrlV2}/audit/crew/transportations/${transportationId}/logs?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    if (lastId) {
      url = `${url}&lastId=${lastId}`;
    }
    return this.http.get<PagedAuditLogLite>(url);
  }

  getFlightPassengers(flightId: number, pageNumber = 1, pageSize = 10) {
    const url = `${this.apiUrlV2}/audit/flights/deleted/${flightId}/pax?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    return this.http.get<PagedFlightPassengers>(url);
  }

  getScheduledFlightPassengers(flightId: number, pageNumber = 1, pageSize = 10) {
    const url = `${this.apiUrlV2}/audit/flights/${flightId}/pax?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    return this.http.get<PagedFlightPassengers>(url);
  }

  getPassengerLogs(paxId: number, pageNumber = 1, pageSize = 10, lastId?: number) {
    let url = `${this.apiUrlV2}/audit/pax/${paxId}/logs?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    if (lastId) {
      url = `${url}&lastId=${lastId}`;
    }
    return this.http.get<PagedAuditLogLite>(url);
  }

  getPassengerCateringLogs(cateringId: number, pageNumber = 1, pageSize = 10, lastId?: number) {
    let url = `${this.apiUrlV2}/audit/pax/catering/${cateringId}/logs?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    if (lastId) {
      url = `${url}&lastId=${lastId}`;
    }
    return this.http.get<PagedAuditLogLite>(url);
  }

  getPassengerHotelLogs(hotelId: number, pageNumber = 1, pageSize = 10, lastId?: number) {
    let url = `${this.apiUrlV2}/audit/pax/hotels/${hotelId}/logs?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    if (lastId) {
      url = `${url}&lastId=${lastId}`;
    }
    return this.http.get<PagedAuditLogLite>(url);
  }

  getPassengerTransportationLogs(transportationId: number, pageNumber = 1, pageSize = 10, lastId?: number) {
    let url = `${this.apiUrlV2}/audit/pax/transportation/${transportationId}/logs?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    if (lastId) {
      url = `${url}&lastId=${lastId}`;
    }
    return this.http.get<PagedAuditLogLite>(url);
  }

  // Tier-1: list of schedulers — deleted trips
  getFlightSchedulers(flightId: number, pageNumber = 1, pageSize = 10) {
    const url = `${this.apiUrlV2}/audit/flights/deleted/${flightId}/schedulers?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    return this.http.get<PagedFlightSchedulers>(url);
  }

  // Tier-1: list of schedulers — scheduled trips
  getScheduledFlightSchedulers(flightId: number, pageNumber = 1, pageSize = 10) {
    const url = `${this.apiUrlV2}/audit/flights/${flightId}/schedulers?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    return this.http.get<PagedFlightSchedulers>(url);
  }

  // Tier-2: audit log for a specific scheduler (shared between scheduled / deleted)
  getSchedulerLogs(schedulerId: number, pageNumber = 1, pageSize = 10, lastId?: number) {
    let url = `${this.apiUrlV2}/audit/schedulers/${schedulerId}/logs?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    if (lastId) {
      url = `${url}&lastId=${lastId}`;
    }
    return this.http.get<PagedAuditLogLite>(url);
  }
}
